<!--
  Orquestador del selector de plantillas de informe (solo frontend, SIN persistencia).
  Nivel 1: tipo de informe. Debajo: galería de plantillas o, al elegir una, el
  customizer + preview. La lógica/datos viven en data/report-template.data.js y la
  UI en views/TemplateGalery.vue y views/TemplateReportDetail.vue.
  Define aquí los tokens --rt-* que heredan los hijos por el DOM.
-->
<template>
  <div class="report-templates">
    <!-- Nivel 1: tipo de informe (hoy solo el detallado; el resto, previsto) -->
    <div class="rt-types" role="tablist" aria-label="Tipo de informe">
      <button
        v-for="rt in reportTypes"
        :key="rt.id"
        type="button"
        role="tab"
        class="rt-type"
        :aria-selected="rt.id === currentType ? 'true' : 'false'"
        :disabled="!rt.available"
        @click="selectType(rt)">
        {{ rt.name }}<span v-if="!rt.available" class="rt-soon">{{ $t('coming_soon') }}</span>
      </button>
    </div>

    <!-- Nivel 2: galería (paso previo), customizer de la plantilla elegida o el
         Constructor de páginas (card final de la galería, ya no pestaña aparte) -->
    <template-galery
      v-if="screen === 'gallery'"
      :active-key="activeKey"
      :active-modified="activeModified"
      :imported-from="importedFrom"
      :imported-chips="importedChips"
      @use-template="onUseTemplate"
      :custom-in-use="customInUse"
      :custom-chips="customChips"
      @open-builder="openBuilder"
      @import-existing="openImport"
      @view-imported="onViewImported" />

    <template-report-detail
      v-else-if="screen === 'customizer'"
      :template="current"
      :project="projectInfo"
      :project-id="projectId"
      :activity-groups="activityGroups"
      @back="backToGallery" />

    <div v-else class="rt-builder-wrap">
      <report-page-builder :project-id="projectId" :activity-groups="activityGroups" :import-pages="importedPages" :import-meta="importMetaForBuilder" force-template-key="custom" @back="backToGallery" />
    </div>

    <!-- Modal: traer configuración de informe de otro proyecto de la empresa.
         Sin scroll-body: la lista y el preview scrollean en sus propios contenedores
         (el modal no se mueve y el footer de acciones queda siempre visible). -->
    <epc-modal-wrapper :active.sync="importOpen" :title="$t('import_report_config')" :width="importWidth">
      <import-from-project
        v-if="importOpen"
        :project-id="projectId"
        @cancel="importOpen = false"
        @select="onImportSelect" />
    </epc-modal-wrapper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { reportTypes, project as demoProject } from './data/report-template.data'
import { loadBuilderConfig } from '../report-page-builder/services/page-builder.service'
import { deriveActiveState } from './services/report-templates.service'
import TemplateGalery from './views/TemplateGalery.vue'
import TemplateReportDetail from './views/TemplateReportDetail.vue'
import ReportPageBuilder from '../report-page-builder/report-page-builder.vue'
import ImportFromProject from './views/ImportFromProject.vue'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'

export default {
  name: 'report-templates',

  components: { TemplateGalery, TemplateReportDetail, ReportPageBuilder, ImportFromProject, EpcModalWrapper },

  props: {
    projectId: { type: [Number, String], default: null },
    activityGroups: { type: Array, default: () => [] },
  },

  data() {
    return {
      reportTypes,
      currentType: 'detailed',
      screen: 'gallery',   // 'gallery' | 'customizer' | 'builder'
      current: null,       // plantilla elegida (objeto)
      activeKey: null,     // plantilla EN USO del proyecto (weekly/executive/custom)
      activeModified: false,
      importOpen: false,   // modal "traer config de otro proyecto"
      importedPages: null, // páginas traídas de otro proyecto (se cargan en el Constructor)
      importMetaForBuilder: null, // { id, name } del proyecto origen al pasar al Constructor
      importedFrom: null,  // { id, name } del proyecto origen de la config EN USO (para la galería)
      importedChips: [],   // etiquetas de las páginas de la config EN USO (para la galería)
      customInUse: false,  // hay config en uso que no es plantilla con nombre ni importada
      customChips: [],     // etiquetas de las páginas de esa config personalizada en uso
    }
  },

  created() { this.loadActive() },

  computed: {
    ...mapGetters({ storeProject: 'app/project', storeCompany: 'app/company' }),

    importWidth() {
      // ~90% del ancho de ventana (Buefy espera px).
      return Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.9)
    },

    // Nombre del proyecto y empresa reales (del store); el resto (semana, % avance)
    // sigue demo hasta que lo cablemos a datos reales.
    projectInfo() {
      return {
        ...demoProject,
        name: (this.storeProject && this.storeProject.name) || demoProject.name,
        company: (this.storeCompany && this.storeCompany.name) || demoProject.company,
      }
    },
  },

  methods: {
    selectType(rt) {
      if (!rt.available || rt.id === this.currentType) return
      this.currentType = rt.id
    },

    onUseTemplate(t) {
      // "Personalizada" abre directamente el Constructor de páginas (control total).
      if (t.key === 'personalizada' || t.key === 'custom') { this.openBuilder(); return }
      this.current = t
      this.screen = 'customizer'
    },

    // Abre el Constructor vacío (sin páginas importadas: carga la config del proyecto).
    openBuilder() {
      this.importedPages = null
      this.importMetaForBuilder = null
      this.screen = 'builder'
    },

    // Abre el modal para traer la configuración de otro proyecto de la empresa.
    openImport() {
      this.importOpen = true
    },

    // El usuario eligió un proyecto: su config se trata como PLANTILLA → se abre el
    // configurador (lista mostrar/ocultar + reordenar + preview + Aplicar).
    onImportSelect({ id, pages, name }) {
      this.importOpen = false
      this.current = { key: null, name: name || this.$t('import_report_config'), sourceId: id || null, locked: false, pages }
      this.screen = 'customizer'
    },

    // Desde la galería (card EN USO): ver/editar la config importada aplicada → abre el
    // configurador con la config guardada actual.
    async onViewImported() {
      if (!this.projectId) return
      try {
        const { builder } = await loadBuilderConfig(this.projectId)
        if (!builder || !builder.pages) return
        const src = builder.importedFrom || this.importedFrom || {}
        this.current = { key: null, name: src.name || this.$t('import_report_config'), sourceId: src.id || null, locked: false, pages: builder.pages }
        this.screen = 'customizer'
      } catch (e) { /* sin config */ }
    },

    // Vuelve a la galería y recarga la plantilla en uso (el chip "En uso" puede
    // haber cambiado tras aplicar en el customizer o guardar en el builder).
    backToGallery() {
      this.screen = 'gallery'
      this.importedPages = null
      this.importMetaForBuilder = null
      this.loadActive()
    },

    // Plantilla/config en uso del proyecto: la derivación la hace el servicio (deriveActiveState)
    // a partir del config cargado; aquí solo se asigna al estado de la vista.
    async loadActive() {
      if (!this.projectId) return
      try {
        const { builder } = await loadBuilderConfig(this.projectId)
        const s = deriveActiveState(builder)
        this.activeKey = s.activeKey
        this.activeModified = s.activeModified
        this.importedFrom = s.importedFrom
        this.importedChips = s.importedChips
        this.customInUse = s.customInUse
        this.customChips = s.customChips
      } catch (e) { /* sin config aún */ }
    },
  },
}
</script>

<style scoped>
/* Tokens del mockup, definidos en el wrapper; los hijos los heredan por el DOM. */
.report-templates {
  --rt-surface: #ffffff; --rt-surface-2: #f6f8fb; --rt-ink: #16203a;
  --rt-muted: #697289; --rt-faint: #9aa3b5; --rt-line: #e2e7ef; --rt-line-strong: #cdd5e2;
  --rt-accent: #2456d6; --rt-accent-ink: #1b3d94; --rt-accent-wash: #e8eefc;
  --rt-ok: #15926a; --rt-warn: #c47a12;
  --rt-shadow: 0 1px 2px rgba(20,32,60,.06), 0 8px 24px rgba(20,32,60,.08);
  --rt-radius: 14px;
  color: var(--rt-ink);
}

/* Nivel 1: tipos */
.rt-types { display: flex; flex-wrap: wrap; gap: 4px; margin: 0 0 16px; border-bottom: 1px solid var(--rt-line); }
.rt-type { appearance: none; border: none; background: none; font: inherit; font-weight: 700; font-size: 14px; color: var(--rt-muted); padding: 10px 14px; border-radius: 9px 9px 0 0; cursor: pointer; position: relative; display: inline-flex; align-items: center; gap: 8px; }
.rt-type[aria-selected="true"] { color: var(--rt-accent-ink); }
.rt-type[aria-selected="true"]::after { content: ""; position: absolute; left: 10px; right: 10px; bottom: -1px; height: 2.5px; background: var(--rt-accent); border-radius: 3px; }
.rt-type[disabled] { color: var(--rt-faint); cursor: not-allowed; }
.rt-soon { font-size: 9px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--rt-faint); background: var(--rt-surface-2); border: 1px solid var(--rt-line); border-radius: 999px; padding: 1px 6px; }

/* Constructor de páginas embebido (antes era una pestaña) */
.rt-builder-wrap { display: flex; flex-direction: column; gap: 12px; }
</style>
